import { computed, inject, Injectable, Signal } from "@angular/core";
import { LanguageConfigStore } from "@app/shared/services/language-config.store";
import { PublishReadinessService } from "../../../services/publish/publish-readiness.service";
import { TabReadinessService } from "../../../services/publish/tab-readiness.service";
import { TabReadinessSummary } from "../../../services/publish/tab-readiness.types";
import { PublishReadiness } from "../../../services/publish/types";
import { ActivePriceStore } from "./active-price.store";
import {
  computeFlatLanguageCoverage,
  FlatLangCoverage,
} from "./flat-language-coverage.util";
import { computeReadinessPercent } from "./language-coverage.util";
import { normalizeTabId, worstStatus } from "./tab-algebra.util";

export type ReadinessTone = "ready" | "warn" | "block";

export interface ReadinessSummary {
  readonly canPublish: boolean;
  readonly errorCount: number;
  readonly warningCount: number;
  readonly label: string;
  readonly tone: ReadinessTone;
}

@Injectable()
export class ActivePriceReadiness {
  private readonly active = inject(ActivePriceStore);
  private readonly publishReadiness = inject(PublishReadinessService);
  private readonly tabReadiness = inject(TabReadinessService);
  private readonly languageConfig = inject(LanguageConfigStore);

  readonly publish: Signal<PublishReadiness> = computed(() =>
    this.publishReadiness.evaluate(this.active.scoped()),
  );

  readonly byTab: Signal<Map<string, TabReadinessSummary>> =
    this.tabReadiness.summary(this.active.scoped);

  /** Section status per canonical tab id. Two sections can map to one tab,
   *  so they merge by the worst status instead of last-write-wins. */
  readonly statusByTab: Signal<Record<string, string>> = computed(() => {
    const map: Record<string, string> = {};
    for (const section of this.publish().sections) {
      const id = normalizeTabId(section.id);
      map[id] = worstStatus(map[id], section.status);
    }
    // Special promotion is optional config, never a publish section, so
    // its step renders complete.
    map["special-promotion"] ??= "complete";
    return map;
  });

  readonly percent = computed(() =>
    computeReadinessPercent(this.publish().sections),
  );

  readonly summary: Signal<ReadinessSummary | null> = computed(() => {
    if (!this.active.scoped()) return null;
    const readiness = this.publish();
    const errorCount = readiness.errors.length;
    const warningCount = readiness.warnings.length;
    let label = "Ready to publish";
    let tone: ReadinessTone = "ready";
    if (errorCount > 0) {
      label = `${errorCount} blocker${errorCount === 1 ? "" : "s"}`;
      tone = "block";
    } else if (warningCount > 0) {
      label = `Ready with ${warningCount} warning${warningCount === 1 ? "" : "s"}`;
      tone = "warn";
    }
    return {
      canPublish: readiness.canPublish,
      errorCount,
      warningCount,
      label,
      tone,
    };
  });

  readonly languageCoverage: Signal<Record<string, FlatLangCoverage>> =
    computed(() =>
      computeFlatLanguageCoverage(
        this.active.scoped()?.__detail,
        this.languageConfig.activeLocales(),
      ),
    );
}
